<template>
  <portal
    v-if="dragging"
    to="main_before"
  >
    <div class="drag-drop-handler dragdrophandler text-noselect">
      <div
        ref="shallowitem"
        class="dragdrophandler_shallowitem"
      >
        <slot name="shallowitem"></slot>
      </div>
      <div
        id="dropindicator"
        class="dragdrophandler_dropindicator"
        :style="dropIndicatorStyle"
      >
        <slot name="dropindicator">
          <div
            v-if="hasDefaultIndicator"
            style="height: 1px; background: var(--clr-primary); width: 100%"
          ></div>
        </slot>
      </div>
    </div>
  </portal>
</template>

<script>
import { nextTick } from 'vue'
import float_mix from '@/mixins/float_mix'
import { throttle } from 'lodash'

export default {
  name: 'DragDropHandler',
  mixins: [float_mix],
  props: {
    dropTargetIdPart: String,
    getIndicatorStyle: {
      default: () => style => style,
    },
    hasDefaultIndicator: {
      type: Boolean,
      default: true,
    },
    proccessEvent: Function,
  },
  emits: ['drag:drop', 'drag:over'],
  data() {
    return {
      dragging: false,
      dropTarget: null,
      dropSurface: null,
      dropIndicatorStyle: 'display: none',
    }
  },
  beforeUnmount() {
    this.removeEventListeners()
  },
  methods: {
    removeEventListeners() {
      window.removeEventListener('mousemove', this.dragOver)
      window.removeEventListener('mouseup', this.endDrag)
      window.removeEventListener('touchmove', this.dragOver)
      window.removeEventListener('touchend', this.endDrag)
    },
    addEventListeners() {
      window.addEventListener('mousemove', this.dragOver, { passive: false })
      window.addEventListener('mouseup', this.endDrag, { passive: false })
      window.addEventListener('touchmove', this.dragOver, { passive: false })
      window.addEventListener('touchend', this.endDrag, { passive: false })
    },
    reset() {
      this.dropTarget = null
      this.dropSurface = null
      this.dropIndicatorStyle = 'display: none'
    },
    startDrag(e) {
      e.preventDefault()
      this.addEventListeners()
      this.dragging = true
      document.body.classList.add('gw_dragging')
      nextTick(() => {
        this.dragOver(e)
      })
    },
    endDrag(e) {
      e.preventDefault()
      this.removeEventListeners()
      document.body.classList.remove('gw_dragging')
      const res = {
        e,
        dropTarget: this.dropTarget,
        dropSurface: this.dropSurface,
        dropTargetIdPart: this.dropTargetIdPart,
      }
      this.$emit('drag:drop', res)
      this.dragging = false
      this.reset()
    },
    dragOver: throttle(function (e) {
      e.preventDefault()
      this.positionShallowEl(e)
      if (!this.dropTargetIdPart) return

      let { clientX, clientY } = e
      if (e?.touches) {
        clientY = e?.touches[0]?.clientY
        clientX = e?.touches[0]?.clientX
      }

      const el = document.elementFromPoint(clientX, clientY)

      const dropTarget = this.getDropTargetEl(el)
      this.dropTarget = dropTarget
      this.$emit('drag:over', dropTarget)
      if (!dropTarget) {
        this.reset()
        return
      }

      const { y, height, width, left, top, bottom } = dropTarget.getBoundingClientRect()
      const dropSurface = clientY >= y + height / 2 ? 'after' : 'before'
      this.dropSurface = dropSurface

      this.dropIndicatorStyle = this.getIndicatorStyle({
        display: 'block',
        top: `${dropSurface === 'before' ? top : bottom}px`,
        left: `${left}px`,
        width: `${width}px`,
      })

      if (this.proccessEvent) {
        this.proccessEvent(e)
      }
    }, 10),
    getDropTargetEl(el) {
      if (!el) return null
      if (el.id === 'dropindicator') {
        return this.dropTarget
      }
      if (el.id.includes(this.dropTargetIdPart)) {
        return el
      }
      if (!el.parentElement) return null
      return this.getDropTargetEl(el.parentElement)
    },
    positionShallowEl(e) {
      e.preventDefault()
      const tooltip = this.$refs.shallowitem
      const boundary = document.body
      if (!tooltip || !boundary) return
      this.fl_followMouse(e, boundary, tooltip, {
        offset: 8,
        padding: 8,
        strategy: 'fixed',
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.dragdrophandler {
  position: fixed;
  z-index: 99;
  &_shallowitem {
    position: fixed;
    z-index: 99;
  }
  &_dropindicator {
    position: fixed;
    z-index: -1;
  }
}
</style>
